/**
 * C.3b — File System Access API import.
 *
 * Calls `window.showDirectoryPicker()` and streams the picked folder's
 * tree as ImportFile entries. Chrome/Edge/Opera support this fully;
 * Safari/Firefox do not (caller should feature-detect via
 * `isShowDirectoryPickerAvailable()` before kicking off).
 *
 * Browser-only: no Node fs, no Express, no NW.js shell.
 */

import { isTextExt } from './walk';
import type { ImportFile, ImportSource } from './types';

export function isShowDirectoryPickerAvailable(): boolean {
  return (
    typeof window !== 'undefined' &&
    'showDirectoryPicker' in window &&
    typeof (window as unknown as { showDirectoryPicker?: unknown }).showDirectoryPicker ===
      'function'
  );
}

interface FileSystemDirectoryHandleLike {
  name: string;
  kind: 'directory';
  values: () => AsyncIterableIterator<FileSystemHandleLike>;
}
interface FileSystemFileHandleLike {
  name: string;
  kind: 'file';
  getFile: () => Promise<File>;
}
type FileSystemHandleLike = FileSystemDirectoryHandleLike | FileSystemFileHandleLike;

async function* walkDirectory(
  dir: FileSystemDirectoryHandleLike,
  prefix: string,
): AsyncIterableIterator<ImportFile> {
  for await (const handle of dir.values()) {
    if (handle.kind === 'directory') {
      yield* walkDirectory(handle, prefix ? `${prefix}/${handle.name}` : handle.name);
    } else {
      const file = await handle.getFile();
      const path = prefix ? `${prefix}/${handle.name}` : handle.name;
      const dot = handle.name.lastIndexOf('.');
      const ext = dot > 0 ? handle.name.slice(dot + 1).toLowerCase() : '';
      const isText = isTextExt(ext);
      const out: ImportFile = {
        path,
        ext,
        sizeBytes: file.size,
        lastModifiedMs: file.lastModified,
        mimeType: file.type || undefined,
      };
      if (isText) {
        out.text = await file.text();
      } else {
        const buf = await file.arrayBuffer();
        out.bytes = new Uint8Array(buf);
      }
      yield out;
    }
  }
}

export interface OpenFsapPickerOptions {
  /** Override the picker dialog title. */
  title?: string;
  /** Suggested starting directory. Browser may ignore. */
  startIn?: 'documents' | 'desktop' | 'downloads';
}

export async function openFsapVaultPicker(
  opts: OpenFsapPickerOptions = {},
): Promise<ImportSource | null> {
  if (!isShowDirectoryPickerAvailable()) return null;
  const w = window as unknown as {
    showDirectoryPicker: (init?: {
      mode?: 'read' | 'readwrite';
      startIn?: string;
      id?: string;
    }) => Promise<FileSystemDirectoryHandleLike>;
  };

  let dir: FileSystemDirectoryHandleLike;
  try {
    dir = await w.showDirectoryPicker({
      mode: 'read',
      startIn: opts.startIn ?? 'documents',
      id: 'repancy-obsidian-vault-import',
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') return null;
    throw err;
  }

  const label = opts.title ?? `Folder: ${dir.name}`;
  return {
    kind: 'fsap',
    label,
    files: () => walkDirectory(dir, ''),
  };
}
